import React, { useState, useEffect, useRef, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import BounceLoader from 'react-spinners/BounceLoader';

// Components
import OrganizationFilters from '../utils/OrganizationFilters';
import AreaFilters from '../utils/AreaFilters';
import SelectButton from '../utils/SelectButton';
import TrackingOverviewMap from './TrackingOverviewMap';
import TrackingRouteMap from './TrackingRouteMap';

// Hooks
import useDidMountEffect from '../../hooks/useDidMountEffect';

// Actions
import {
	get_organizations,
	get_workspaces,
	get_modules,
	get_cycles,
	get_areas,
	get_areas_mine
} from '../../actions/organizationActions';
import { get_single_route_tracking_history, get_tracking_overview, get_route_locations_schedule } from '../../actions/trackingActions';
import { getDayInitial } from '../../utils/datesHandler';

const Tracking = () => {
	const dispatch = useDispatch();

	const _ls = { ...localStorage };

	const { user, isAdminUser, authChecked } = useSelector(state => state.auth);
	const { global_data } = useSelector(state => state.global);
	const { organizations, workspaces, modules, cycles, areas, areasLoading } = useSelector(state => state.organization);
	const { history, loading, overview, overviewLoading, overviewCount, schedule, scheduleMeta } = useSelector(state => state.tracking);
	const { staff, staffChecked } = useSelector(state => state.staff);

	const [orgFilters, setOrgFilters] = useState({ organization: '', workspace: '', module: '', cycle: '' });
	const [areaFilters, setAreaFilters] = useState({ region: '', zone: '', depot: '', group: '' });
	const [areaLabels, setAreaLabels] = useState({ region: '', zone: '', depot: '', group: '' });
	const [selectedRoute, setSelectedRoute] = useState('');
	const [selectedDate, setSelectedDate] = useState(() => {
		const today = new Date();
		const year = today.getFullYear();
		const month = String(today.getMonth() + 1).padStart(2, '0');
		const day = String(today.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}); // Default to today (local timezone)
	const [disableInputs, setDisableInputs] = useState(true);
	const [displayData, setDisplayData] = useState(false);
	const [viewMode, setViewMode] = useState('overview'); // 'overview' or 'route'
	const [selectedRouteInfo, setSelectedRouteInfo] = useState(null);
	const [mapKey, setMapKey] = useState(0); // Key to force map re-render

	const [pendingSearch, setPendingSearch] = useState(false);
	const skipHandleRequestsRef = useRef(false);
	const [initialAreas, setInitialAreas] = useState(null);
	const areasDispatchedForRef = useRef(null);

	useEffect(() => {
		dispatch(get_organizations());
	}, []);

	useEffect(() => {
		let _organization = '';

		if (organizations.length <= 1) {
			_organization = user.organization;
		}
		else {
			_organization = _ls?.organization;
		}

		setOrgFilters({
			...orgFilters,
			organization: _organization,
			workspace: _ls?.workspace || '',
			module: _ls?.module || '',
			cycle: _ls?.cycle || ''
		});
	}, [organizations]);

	useDidMountEffect(() => {
		if (orgFilters.organization !== '') {
			dispatch(get_workspaces({ organization: orgFilters.organization }));
		}
	}, [orgFilters.organization]);

	const onChangeOrgFilters = (e, name) => {
		let filters = {...orgFilters};
		let keys = Object.keys(filters);

		let idx = keys.indexOf(name);
		for (let index = idx; index < keys.length; index++) {
			const key = keys[index];
			filters[key] = '';
			localStorage.setItem(key, '');
		}

		if (e !== null){
			filters[name] = e.value;
			localStorage.setItem(name, e.value);
		}

		skipHandleRequestsRef.current = true;
		setOrgFilters(filters);
		setAreaFilters({ region: '', zone: '', depot: '', group: '' });
		setAreaLabels({ region: '', zone: '', depot: '', group: '' });
		['region', 'zone', 'depot', 'group'].forEach(k => localStorage.setItem(k, ''));
		setSelectedRoute('');
	}

	const onChangeAreaFilters = (e, name) => {
		let filters = {...areaFilters};
		let labels = {...areaLabels};
		let keys = Object.keys(filters);

		if (e === null){
			let idx = keys.indexOf(name);
			for (let index = idx; index < keys.length; index++) {
				const key = keys[index];
				filters[key] = '';
				labels[key] = '';
				localStorage.setItem(key, '');
			}
		}
		else {
			filters[name] = e.value;
			labels[name] = e.label;
			localStorage.setItem(name, e.value);
		}

		setAreaFilters(filters);
		setAreaLabels(labels);
		setSelectedRoute('');
	}

	useDidMountEffect(() => {
		setDisplayData(false);

		if (orgFilters.organization !== '') {
			dispatch(get_modules({ organization: orgFilters.organization, workspace: orgFilters.workspace }));
		}

		if (orgFilters.workspace !== '') {
			if (orgFilters.module !== '') {
				dispatch(get_cycles({ workspace: orgFilters.workspace, module: orgFilters.module }));
			}
		}
	}, [orgFilters]);

	useDidMountEffect(() => {
		if (orgFilters.module !== '' && orgFilters.cycle !== ''){
			setDisableInputs(false);
		}
		else {
			setDisableInputs(true);
			setDisplayData(false);
		}
	}, [orgFilters]);

	useDidMountEffect(() => {
		if (!authChecked) return;
		if (orgFilters.cycle !== '') {
			const areasAction = isAdminUser ? get_areas : get_areas_mine;
			areasDispatchedForRef.current = `${orgFilters.workspace}|${orgFilters.cycle}`;
			dispatch(areasAction(orgFilters.workspace, orgFilters.cycle, areaFilters));
		}
	}, [orgFilters, areaFilters, isAdminUser, authChecked]);

	useEffect(() => {
		const currentStaffType = staff?.staff_type ?? 0;
		const levels = [
			{ key: 'region', source: areas?.regions, scopeMin: 4 },
			{ key: 'zone', source: areas?.zones, scopeMin: 8 },
			{ key: 'depot', source: areas?.depots, scopeMin: 16 },
			{ key: 'group', source: areas?.groups, scopeMin: 32 }
		];

		const preselectedFilters = {};
		const preselectedLabels = {};

		levels.forEach(({ key, source, scopeMin }) => {
			if (currentStaffType < scopeMin) return;
			if (!Array.isArray(source) || source.length !== 1) return;

			const value = source[0]?._id?.$oid;
			const label = source[0]?.name;
			if (!value) return;
			if (areaFilters[key] === value) return;

			preselectedFilters[key] = value;
			preselectedLabels[key] = label ?? '';
			localStorage.setItem(key, value);
		});

		if (Object.keys(preselectedFilters).length === 0) return;

		skipHandleRequestsRef.current = true;
		setAreaFilters(prev => ({ ...prev, ...preselectedFilters }));
		setAreaLabels(prev => ({ ...prev, ...preselectedLabels }));
	}, [areas, staff]);

	useDidMountEffect(() => {
		setInitialAreas(null);
		areasDispatchedForRef.current = null;
	}, [orgFilters.workspace, orgFilters.module, orgFilters.cycle]);

	useEffect(() => {
		if (initialAreas) return;
		if (areasLoading) return;
		if (areasDispatchedForRef.current !== `${orgFilters.workspace}|${orgFilters.cycle}`) return;
		if (!areas || typeof areas !== 'object' || Array.isArray(areas)) return;

		const noFiltersApplied = Object.values(areaFilters).every(v => v === '');
		if (!noFiltersApplied) return;

		setInitialAreas(areas);
	}, [areas, areaFilters, initialAreas, areasLoading, orgFilters.workspace, orgFilters.cycle]);

	const searchRoutes = (e) => {
		e.preventDefault();
		setSelectedRoute('');
		setViewMode('overview');
		setSelectedRouteInfo(null);
		setPendingSearch(true);
	}

	const runOverviewFetch = () => {
		let cycle = cycles.find(c => c._id.$oid === orgFilters.cycle);
		if (!cycle) return;

		setDisplayData(true);
		dispatch(get_tracking_overview(orgFilters, areaFilters));
	}

	useEffect(() => {
		if (!pendingSearch) return;
		if (!staffChecked) return;
		if (areasLoading) return;
		if (!areas || typeof areas !== 'object' || Array.isArray(areas)) return;

		const currentStaffType = staff?.staff_type ?? 0;
		const scopeMap = { regions: 4, zones: 8, depots: 16, groups: 32 };
		const hasPendingPreselection = Object.entries(scopeMap).some(([key, scopeMin]) => {
			if (currentStaffType < scopeMin) return false;
			const arr = areas?.[key];
			if (!Array.isArray(arr) || arr.length !== 1) return false;
			const id = arr[0]?._id?.$oid;
			if (!id) return false;
			const filterKey = key.slice(0, -1);
			return areaFilters[filterKey] !== id;
		});
		if (hasPendingPreselection) return;

		runOverviewFetch();
		setPendingSearch(false);
	}, [pendingSearch, areasLoading, areas, areaFilters, staff, staffChecked]);

	// Reload overview when area filters change (only in overview mode)
	useDidMountEffect(() => {
		if (skipHandleRequestsRef.current) {
			skipHandleRequestsRef.current = false;
			return;
		}
		if (displayData && viewMode === 'overview') {
			dispatch(get_tracking_overview(orgFilters, areaFilters));
		}
	}, [areaFilters]);

	const onChangeRoute = (e) => {
		if (e !== null) {
			setSelectedRoute(e.value);
			// Find the route object to get the user
			const routeObj = routes.find(r => r._id.$oid === e.value);
			setSelectedRouteInfo(routeObj);
			fetchTrackingHistory(e.value, routeObj?.user?.$oid, selectedDate);
		} else {
			setSelectedRoute('');
			setSelectedRouteInfo(null);
		}
	}

	const onBackToOverview = () => {
		setViewMode('overview');
		setSelectedRoute('');
		setSelectedRouteInfo(null);
		skipHandleRequestsRef.current = true;
		setAreaFilters({ region: '', zone: '', depot: '', group: '' });
		setAreaLabels({ region: '', zone: '', depot: '', group: '' });
		['region', 'zone', 'depot', 'group'].forEach(k => localStorage.setItem(k, ''));
		setMapKey(prev => prev + 1); // Force map re-render
	}

	const onGoToRouteDetail = () => {
		setViewMode('route');
		skipHandleRequestsRef.current = true;
		setAreaFilters({ region: '', zone: '', depot: '', group: '' });
		setAreaLabels({ region: '', zone: '', depot: '', group: '' });
		['region', 'zone', 'depot', 'group'].forEach(k => localStorage.setItem(k, ''));
	}

	const onChangeDate = (e) => {
		const newDate = e.target.value;
		setSelectedDate(newDate);

		// If a route is selected, fetch data for the new date
		if (selectedRoute) {
			const routeObj = routes.find(r => r._id.$oid === selectedRoute);
			fetchTrackingHistory(selectedRoute, routeObj?.user?.$oid, newDate);
		}
	}

	const fetchTrackingHistory = (routeId, userId, date) => {
		// Convert selected date to epoch timestamps in seconds (local timezone)
		const [year, month, day] = date.split('-');
		const startDateObj = new Date(year, month - 1, day, 0, 0, 0, 0);
		const endDateObj = new Date(year, month - 1, day, 23, 59, 59, 999);

		const startDate = Math.floor(startDateObj.getTime() / 1000);
		const endDate = Math.floor(endDateObj.getTime() / 1000);

		const historyFilters = {
			user: userId,
			route: routeId,
			module: orgFilters.module,
			start_date: startDate,
			end_date: endDate
		};

		dispatch(get_single_route_tracking_history(historyFilters));

		const dayInitial = getDayInitial(date);
		dispatch(get_route_locations_schedule(routeId, dayInitial));
	}

	// Obtener rutas del objeto areas y filtrar por status === 2
	const allRoutes = areas?.routes || [];
	const routes = allRoutes.filter(route => route.status === 2);

	// Get selected cycle for date limits
	const selectedCycle = cycles.find(c => c._id?.$oid === orgFilters.cycle);

	// Calculate min and max dates for the date input
	const getDateLimits = () => {
		const today = new Date();
		const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

		let minDate = '';
		let maxDate = todayStr;

		if (selectedCycle) {
			// Cycle start date
			if (selectedCycle.start?.$date) {
				const startDate = new Date(selectedCycle.start.$date);
				minDate = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
			}

			// Cycle end date - use the earlier of cycle end or today
			if (selectedCycle.end?.$date) {
				const endDate = new Date(selectedCycle.end.$date);
				const endDateStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
				maxDate = endDateStr < todayStr ? endDateStr : todayStr;
			}
		}

		return { minDate, maxDate };
	};

	const { minDate, maxDate } = getDateLimits();

	const staffType = isAdminUser ? 0 : (staff?.staff_type ?? 0);
	const isEmptyLevel = (arr) => !Array.isArray(arr) || arr.length === 0;
	const areasForVisibility = initialAreas || areas;
	const hiddenLevels = {
		region: staffType >= 4 || isEmptyLevel(areasForVisibility?.regions),
		zone: staffType >= 8 || isEmptyLevel(areasForVisibility?.zones),
		depot: staffType >= 16 || isEmptyLevel(areasForVisibility?.depots),
		group: staffType >= 32 || isEmptyLevel(areasForVisibility?.groups)
	};

	return (
		<div className='m-3 pb-5'>
			<h2 className='text-center'>Tracking</h2>
			<p className='text-center'>Visualiza el historial de rutas en el mapa</p>

			{/* Organization Filters */}
			<OrganizationFilters
				orgFilters={orgFilters}
				onChangeOrgFilters={onChangeOrgFilters}
				organizations={organizations}
				workspaces={workspaces}
				modules={modules}
				cycles={cycles}
			/>

			<div className='d-grid gap-2 col-2 mx-auto text-center mt-4'>
				<button className='btn submit-btn' type='button' disabled={disableInputs} onClick={searchRoutes}>
					{(overviewLoading || pendingSearch)
						? <div className='d-flex row align-items-center justify-content-center'>
								<BounceLoader color='#ffffff' size={20}/>
							</div>
						: <div>{global_data?.actionButtons?.search || 'Buscar'}</div>
					}
				</button>
			</div>

			{displayData && (
				<Fragment>
					<hr className='hr-style'/>

					{/* View Toggle */}
					<div className='d-flex justify-content-center align-items-center mt-4 mb-4'>
						<div className='btn-group' role='group'>
							<button
								type='button'
								className={`btn ${viewMode === 'overview' ? 'submit-btn' : 'btn-outline-secondary'}`}
								onClick={onBackToOverview}
							>
								<i className='bi bi-geo-alt-fill me-2'></i>Vista General
							</button>
							<button
								type='button'
								className={`btn ${viewMode === 'route' ? 'submit-btn' : 'btn-outline-secondary'}`}
								onClick={onGoToRouteDetail}
							>
								<i className='bi bi-signpost-2-fill me-2'></i>Detalle de Ruta
							</button>
						</div>
					</div>

					{viewMode === 'overview' ? (
						// Overview Map with Clusters
						<Fragment>
							{/* Area Filters for Overview */}
							<AreaFilters
								areaFilters={areaFilters}
								areaLabels={areaLabels}
								onChangeAreaFilters={onChangeAreaFilters}
								areas={areas}
								disableInputs={disableInputs}
								areasLoading={areasLoading}
								hiddenLevels={hiddenLevels}
							/>

							{overviewLoading ? (
								<div className='d-flex justify-content-center align-items-center' style={{ height: '600px' }}>
									<BounceLoader color='#007bff' size={60}/>
								</div>
							) : overview && overview.length > 0 ? (
								<div className='mt-4 pb-5'>
									<h5 className='text-center mb-3'>Vista General de Tracking</h5>
									<div className='text-center mb-2'>
										<small className='text-muted'>
											Mostrando la última ubicación de {overviewCount.toLocaleString(localStorage.getItem('i18nextLng'))} rutas
										</small>
									</div>
									<TrackingOverviewMap key={mapKey} locations={overview} />
								</div>
							) : (
								<div className='alert alert-secondary text-center mt-4'>
									No hay datos de tracking disponibles
								</div>
							)}
						</Fragment>
					) : (
						// Route Detail View with Filters and Map
						<Fragment>
							<h4 className='text-center mt-4 mb-3'>{global_data?.overview?.filterBy || 'Filtrar por'}:</h4>

							{/* Area Filters */}
							<AreaFilters
								areaFilters={areaFilters}
								areaLabels={areaLabels}
								onChangeAreaFilters={onChangeAreaFilters}
								areas={areas}
								disableInputs={disableInputs}
								areasLoading={areasLoading}
								hiddenLevels={hiddenLevels}
							/>

							{/* Route Selector and Date Picker */}
							<div className='d-flex row align-items-center justify-content-center mt-4 mb-4'>
								<SelectButton
									label="Ruta"
									options={routes}
									name='route'
									value={selectedRoute}
									onChange={onChangeRoute}
									disable={routes.length === 0}
									orgFilter={false}
								/>
								<div className='col-lg-2 col-md-12 col-sm-12 mb-2'>
									<div className='d-flex row align-items-center'>
										<div className='col-lg-3 col-md-3 col-sm-12 text-center'>
											<label>Fecha</label>
										</div>
										<div className='col-lg-9 col-md-9 col-sm-12'>
											<input
												type='date'
												className='form-control'
												value={selectedDate}
												onChange={onChangeDate}
												disabled={!selectedRoute}
												min={minDate}
												max={maxDate}
											/>
										</div>
									</div>
								</div>
							</div>

							<hr className='hr-style'/>

							{/* Route Detail Map */}
							{!selectedRoute ? (
								<div className='alert alert-secondary text-center mt-4'>
									Selecciona una ruta para ver el detalle de tracking
								</div>
							) : loading ? (
								<div className='d-flex justify-content-center align-items-center' style={{ height: '600px' }}>
									<BounceLoader color='#007bff' size={60}/>
								</div>
							) : history && history.length > 0 ? (
								<div className='mt-4 pb-5'>
									<div className='text-center mb-3'>
										<h5>Ruta: {selectedRouteInfo?.route?.code || selectedRouteInfo?.code || 'Detalle'}</h5>
										<small className='text-muted'>
											<i className='bi bi-geo-alt-fill text-danger me-1'></i>
											{history.length} ubicaciones
											{schedule && schedule.length > 0 && (
												<span className='ms-3'>
													| <i className='bi bi-shop text-success ms-3 me-1'></i>
													{scheduleMeta?.count ?? schedule.length} de {scheduleMeta?.total ?? schedule.length} tiendas programadas
													{scheduleMeta?.missing > 0 && (
														<span className='ms-2 text-warning'>({scheduleMeta.missing} sin ubicación)</span>
													)}
												</span>
											)}
										</small>
									</div>
									<TrackingRouteMap locations={history} scheduleLocations={schedule} />
								</div>
							) : (
								<div className='alert alert-info text-center mt-4'>
									No hay datos de tracking disponibles para esta ruta en la fecha seleccionada
								</div>
							)}
						</Fragment>
					)}
				</Fragment>
			)}
		</div>
	);
}

export default Tracking;
